/**
 * Typed fetch client + the gold-standard refresh-token ROTATION flow.
 *
 * Server contract (server/src/modules/iam/*):
 *   - API_PREFIX = "/api/v1" (server/src/platform/http/index.js).
 *   - POST /auth/login   { email, password, appareilId?, laboratoireId? } -> TokenPair
 *   - POST /auth/refresh { refresh_token }                                 -> TokenPair (rotated)
 *   - access JWT TTL 15 min, refresh opaque + rotateOnUse + reuse->revoke-family.
 *   - every response is { success, data } | { success, code, message, details? }.
 *
 * Flow:
 *   1. attach Bearer access token; if it's expiring, proactively refresh first.
 *   2. on a 401 / SESSION_EXPIRED from a normal call, run a SINGLE in-flight refresh, then retry once.
 *   3. on refresh failure -> notifySessionEnded() (auth store purges cache + routes to /login).
 */
import { ApiError, ERROR_CODES, type Envelope, type TokenPair } from "./types";
import { tokenStore } from "./tokenStore";
import { solveCaptcha, type CaptchaProof } from "./captcha";

export const API_BASE = "/api/v1";

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Query;
  /** Skip the auth header + the refresh retry (used by login/refresh themselves). */
  anonymous?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

function buildUrl(path: string, query?: Query): string {
  const base = `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
  if (!query) return base;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null) qs.append(k, String(v));
  }
  const s = qs.toString();
  return s ? `${base}?${s}` : base;
}

async function parseEnvelope<T>(res: Response): Promise<T> {
  let payload: Envelope<T> | undefined;
  try {
    payload = (await res.json()) as Envelope<T>;
  } catch {
    // Non-JSON (proxy down, HTML error page, 204) — synthesize a typed error / empty.
    if (res.ok) return undefined as T;
    throw new ApiError(ERROR_CODES.INTERNAL, `Réponse non-JSON (${res.status})`, res.status);
  }
  if (payload && payload.success) return payload.data;
  const fail = payload as Extract<Envelope<T>, { success: false }> | undefined;
  throw new ApiError(
    fail?.code ?? ERROR_CODES.INTERNAL,
    fail?.message ?? "Erreur inattendue",
    res.status,
    fail?.details,
  );
}

// ── Single in-flight refresh (de-duplicates concurrent 401s) ───────────────────
let refreshInFlight: Promise<TokenPair> | null = null;

async function doRefresh(): Promise<TokenPair> {
  const refresh_token = tokenStore.getRefreshToken();
  if (!refresh_token) {
    throw new ApiError(ERROR_CODES.SESSION_EXPIRED, "Session expirée.", 401);
  }
  const res = await fetch(buildUrl("/auth/refresh"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token }),
  });
  const pair = await parseEnvelope<TokenPair>(res);
  tokenStore.setFromPair(pair);
  return pair;
}

export function refreshSession(): Promise<TokenPair> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function rawFetch<T>(path: string, opts: RequestOptions): Promise<T> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (!opts.anonymous) {
    const token = tokenStore.getAccessToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  const init: RequestInit = {
    method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
    headers,
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  if (opts.signal) init.signal = opts.signal;
  const res = await fetch(buildUrl(path, opts.query), init);
  return parseEnvelope<T>(res);
}

/**
 * The public request entrypoint. Adds proactive refresh + reactive single-retry-after-refresh.
 */
export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  if (opts.anonymous) return rawFetch<T>(path, opts);

  // Proactive: refresh a stale-but-present session before spending a call on a guaranteed 401.
  if (tokenStore.getRefreshToken() && tokenStore.isExpiredOrExpiring()) {
    try {
      await refreshSession();
    } catch {
      tokenStore.notifySessionEnded();
      throw new ApiError(ERROR_CODES.SESSION_EXPIRED, "Session expirée.", 401);
    }
  }

  try {
    return await rawFetch<T>(path, opts);
  } catch (err) {
    if (err instanceof ApiError && err.isUnauthenticated && tokenStore.getRefreshToken()) {
      // Reactive: one refresh, one retry. A second 401 means the session is truly gone.
      try {
        await refreshSession();
      } catch {
        tokenStore.notifySessionEnded();
        throw err;
      }
      try {
        return await rawFetch<T>(path, opts);
      } catch (retryErr) {
        if (retryErr instanceof ApiError && retryErr.isUnauthenticated) {
          tokenStore.notifySessionEnded();
        }
        throw retryErr;
      }
    }
    throw err;
  }
}

/** Ergonomic verb helpers — feature data layers use these (or the typed `api` below). */
export const api = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) =>
    apiRequest<T>(path, { method: "GET", ...(query ? { query } : {}), ...(signal ? { signal } : {}) }),
  post: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: "POST", ...(body !== undefined ? { body } : {}) }),
  put: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: "PUT", ...(body !== undefined ? { body } : {}) }),
  patch: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: "PATCH", ...(body !== undefined ? { body } : {}) }),
  delete: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: "DELETE", ...(body !== undefined ? { body } : {}) }),
};

/**
 * Anonymous login call (no Bearer). Returns the token pair; caller seeds the auth store.
 *
 * CAPTCHA-GATED in production (server/src/platform/captcha → POST /auth/login). The proof rides in the
 * body alongside the credentials. When the UI already minted a proof via the visible CaptchaField, it
 * passes it as `captcha`; otherwise (a programmatic login) we `await solveCaptcha()` so the call still
 * clears the gate. In dev/test the gate is a server-side no-op and the proof is empty `{}`, so the body
 * is unchanged and the request proceeds exactly as before.
 * @param captcha the proof minted by the visible widget (the production-login fix). Falls back to a
 *   transparent `solveCaptcha()` when absent so a headless/programmatic login is unaffected.
 */
export async function loginRequest(
  payload: {
    email: string;
    password: string;
    appareilId?: string;
    /** Numeric central-path lab id (existing). */
    laboratoireId?: string | number;
    /** SaaS lab code — the human Pharmakon-issued `Laboratoire.reference` (multi-lab login). */
    laboratoireRef?: string;
  },
  captcha?: CaptchaProof,
): Promise<TokenPair> {
  const proof = captcha ?? (await solveCaptcha());
  return apiRequest<TokenPair>("/auth/login", {
    method: "POST",
    body: { ...payload, ...proof },
    anonymous: true,
  });
}

/** Authed logout (best-effort; the server revokes the refresh family). */
export function logoutRequest(): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>("/auth/logout", { method: "POST" });
}

/**
 * Anonymous "forgot password" request (server: POST /auth/password/reset-request, publicRoute +
 * authLimiter). Enumeration-safe by contract — the server returns the SAME ack whether or not the
 * e-mail matches, so the caller always shows the same neutral confirmation. The data shape is
 * intentionally `unknown` (the ack body carries nothing the UI branches on).
 */
export function requestPasswordReset(email: string): Promise<unknown> {
  return apiRequest<unknown>("/auth/password/reset-request", {
    method: "POST",
    body: { email },
    anonymous: true,
  });
}

/**
 * Anonymous "reset password" consume (server: POST /auth/password/reset, publicRoute + authLimiter).
 * Trades the one-time token from the e-mail link for the new password; an expired/used token returns
 * a typed failure the caller surfaces as an invalid-link state.
 */
export function confirmPasswordReset(payload: {
  token: string;
  new_password: string;
}): Promise<unknown> {
  return apiRequest<unknown>("/auth/password/reset", {
    method: "POST",
    body: payload,
    anonymous: true,
  });
}

/** The LAB self-signup payload (DemandeAccesPage → public onboarding endpoint). */
export interface LabAccessRequest {
  labName: string;
  ownerName: string;
  email: string;
  phone?: string;
  wilaya: string;
  commune?: string;
  message?: string;
}

/**
 * Anonymous LAB account-request (server: POST /api/v1/public/onboarding/lab-request). CAPTCHA-GATED in
 * production: before posting, `solveCaptcha` mints + solves the stateless PoW challenge (or carries the
 * Turnstile widget token) and the proof rides in the body. In dev/test the gate is a server-side no-op,
 * so `solveCaptcha` returns an empty proof and the request is unchanged (the existing E2E never sees a
 * challenge). The endpoint's persisted-intake side is a LATER admin phase: a 404 / NOT_FOUND /
 * NOT_IMPLEMENTED still surfaces as a typed `ApiError` the caller maps to the optimistic "pending"
 * fallback. camelCase body; the server validator owns canonicalisation.
 * @param turnstileToken the Turnstile widget token, when the cloud provider is configured client-side.
 */
export async function requestLabAccess(
  payload: LabAccessRequest,
  turnstileToken?: string,
): Promise<unknown> {
  const proof = await solveCaptcha(turnstileToken);
  return apiRequest<unknown>("/public/onboarding/lab-request", {
    method: "POST",
    body: { ...payload, ...proof },
    anonymous: true,
  });
}
